import { tenantDataService } from './tenantDataService';
import { calculateLeaveDays, getLeaveYearBoundaries } from '../utils/leaveHelpers';

/**
 * Service for calculating leave statistics for staff members
 */
class LeaveCalculationService {
  /**
   * Get comprehensive leave overview for current user
   * @param {string} userId - Current user ID
   * @param {string} tenantId - Current tenant ID
   * @returns {Promise<Object>} Leave overview data
   */
  async getLeaveOverview(userId, tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant context required');
      }

      // Set tenant context
      tenantDataService.setTenantContext(tenantId);

      // Get leave settings
      const settingsDoc = await tenantDataService.getById('settings', 'clinic');
      const leaveSettings = settingsDoc?.leaveSettings || {};

      // Get all leave requests for current user
      const allLeaveRequests = await tenantDataService.getAll('leaveRequests');
      const userLeaveRequests = allLeaveRequests.filter(request => 
        request.staffId === userId || request.userId === userId
      );

      // Get current leave year boundaries
      const leaveYear = getLeaveYearBoundaries(leaveSettings);
      
      // Filter for current leave year and approved requests only
      const currentYearApprovedRequests = userLeaveRequests.filter(request => {
        const requestDate = new Date(request.startDate);
        return requestDate >= leaveYear.start && 
               requestDate <= leaveYear.end && 
               request.status === 'approved';
      });

      // Calculate used days by type
      const usedDays = {
        leave: 0,
        sick: 0,
        personal: 0,
        emergency: 0,
        maternity: 0
      };

      currentYearApprovedRequests.forEach(request => {
        const days = request.daysCount || this.calculateDays(request.startDate, request.endDate);
        if (usedDays.hasOwnProperty(request.type)) {
          usedDays[request.type] += days;
        }
      });

      // Get allowances from settings
      const allowances = {
        leave: leaveSettings.annualLeaveAllowance || 25,
        sick: leaveSettings.sickLeaveAllowance || 10,
        personal: leaveSettings.personalLeaveAllowance || 5,
        emergency: leaveSettings.emergencyLeaveAllowance || 3,
        maternity: leaveSettings.maternityLeaveAllowance || 90
      };

      // Calculate remaining days
      const remainingDays = {};
      Object.keys(allowances).forEach(type => {
        remainingDays[type] = Math.max(0, allowances[type] - usedDays[type]);
      });

      // Calculate next renewal date
      const nextRenewalDate = new Date(leaveYear.end.getTime() + 24 * 60 * 60 * 1000); // Day after current leave year ends
      const daysUntilRenewal = Math.ceil((nextRenewalDate - new Date()) / (1000 * 60 * 60 * 24));

      // Get recent leave requests (last 5)
      const recentRequests = userLeaveRequests
        .sort((a, b) => new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate))
        .slice(0, 5);

      return {
        leaveYear: {
          start: leaveYear.start,
          end: leaveYear.end,
          daysUntilRenewal: Math.max(0, daysUntilRenewal)
        },
        allowances,
        usedDays,
        remainingDays,
        recentRequests,
        totalUsed: Object.values(usedDays).reduce((sum, days) => sum + days, 0),
        totalAllowance: Object.values(allowances).reduce((sum, days) => sum + days, 0)
      };
    } catch (error) {
      console.error('Error calculating leave overview:', error);
      throw error;
    }
  }

  /**
   * Calculate number of days between two dates (inclusive)
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   * @returns {number} Number of days
   */
  calculateDays(startDate, endDate) {
    if (!startDate || !endDate) return 1;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
    
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  /**
   * Get leave type display information
   * @param {string} type - Leave type
   * @returns {Object} Display information with icon, label, and color
   */
  getLeaveTypeInfo(type) {
    const typeInfo = {
      leave: {
        icon: '🏖️',
        label: 'Annual Leave',
        color: 'blue',
        description: 'Vacation and holiday time'
      },
      sick: {
        icon: '🤒',
        label: 'Sick Leave',
        color: 'red',
        description: 'Medical leave and recovery time'
      },
      personal: {
        icon: '📋',
        label: 'Personal Leave',
        color: 'purple',
        description: 'Personal matters and appointments'
      },
      emergency: {
        icon: '🚨',
        label: 'Emergency Leave',
        color: 'orange',
        description: 'Urgent family or personal emergencies'
      },
      maternity: {
        icon: '👶',
        label: 'Maternity/Paternity',
        color: 'pink',
        description: 'Parental leave for new parents'
      }
    };

    return typeInfo[type] || {
      icon: '📅',
      label: type.charAt(0).toUpperCase() + type.slice(1),
      color: 'gray',
      description: 'Other leave type'
    };
  }
}

export const leaveCalculationService = new LeaveCalculationService();
